import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { showBadgeUnlocks } from "@/lib/badges";
import { CalendarCheck, CheckCircle } from "lucide-react";

function RatingRow({ label, value, onChange }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-600 mb-1.5">{label}</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`w-9 h-9 rounded-lg text-sm font-semibold transition-colors ${value >= n ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function formatWeekLabel(weekStart) {
  if (!weekStart) return "cette semaine";
  try {
    const start = new Date(`${weekStart}T00:00:00`);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${start.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} – ${end.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}`;
  } catch {
    return weekStart;
  }
}

function getWeekStart() {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

export default function WeeklyCheckinCard() {
  const [form, setForm] = useState({
    weight: "",
    mood: 0,
    energy: 0,
    sleep_quality: 0,
    adherence: 0,
    notes: "",
  });
  const [weekStart, setWeekStart] = useState(getWeekStart());
  const [doneThisWeek, setDoneThisWeek] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/checkins/current")
      .then((r) => {
        if (r.data) {
          setForm({
            weight: r.data.weight ?? "",
            mood: r.data.mood || 0,
            energy: r.data.energy || 0,
            sleep_quality: r.data.sleep_quality || 0,
            adherence: r.data.adherence || 0,
            notes: r.data.notes || "",
          });
          setWeekStart(r.data.week_start || getWeekStart());
          setDoneThisWeek(true);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    if (!form.weight && !form.mood && !form.energy) {
      toast.error("Indiquez au moins le poids ou une note");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        week_start: weekStart,
        weight: form.weight ? parseFloat(form.weight) : null,
        mood: form.mood || null,
        energy: form.energy || null,
        sleep_quality: form.sleep_quality || null,
        adherence: form.adherence || null,
        notes: form.notes || null,
      };
      const res = await api.post("/checkins", payload);
      setDoneThisWeek(true);
      toast.success("Check-in hebdomadaire enregistré !");
      showBadgeUnlocks(res.data?.new_badges, toast);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'enregistrer le check-in");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <Card className="border-blue-100" data-testid="weekly-checkin-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {doneThisWeek ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <CalendarCheck className="w-5 h-5 text-blue-600" />
            )}
            <div>
              <h3 className="font-semibold text-slate-900">Check-in hebdomadaire</h3>
              <p className="text-xs text-slate-400">Semaine du {formatWeekLabel(weekStart)}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-xs font-medium text-slate-600 mb-1.5">Poids (kg)</p>
            <Input
              type="number"
              step="0.1"
              value={form.weight}
              onChange={(e) => setForm({ ...form, weight: e.target.value })}
              placeholder="Ex: 72.5"
              className="h-10"
            />
          </div>
          <RatingRow label="Humeur" value={form.mood} onChange={(v) => setForm({ ...form, mood: v })} />
          <RatingRow label="Énergie" value={form.energy} onChange={(v) => setForm({ ...form, energy: v })} />
          <RatingRow label="Sommeil" value={form.sleep_quality} onChange={(v) => setForm({ ...form, sleep_quality: v })} />
          <RatingRow label="Adhérence au plan" value={form.adherence} onChange={(v) => setForm({ ...form, adherence: v })} />
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Comment s'est passée votre semaine ?"
            rows={2}
            className="resize-none text-sm"
          />
          <Button
            onClick={submit}
            disabled={submitting}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full"
          >
            {submitting ? "..." : doneThisWeek ? "Mettre à jour" : "Valider le check-in"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}